/**
 * What a support call is, once it has been booked.
 *
 * Kept apart from `settings.types` because it is a different subject: that file
 * is the dietitian's own profile — who they are and what the practice is called
 * — and this is a diary entry.
 */

/**
 * Where a booking stands.
 *
 * Cal's own, narrowed to what the history has to draw: a call that is still
 * ahead, one that happened, one that will not. Nothing here distinguishes a
 * call somebody missed from one they attended — Cal knows, we do not ask, and a
 * row that guessed would be worse than one that does not say.
 *
 * `Rescheduled` is a call still ahead, like `Scheduled`, and everything the
 * screen offers on one it offers on the other — see `isUpcomingStatus`. It is a
 * separate word only because the reader needs it: a call whose time has moved
 * since it was booked is the one an old invite in somebody's inbox now lies
 * about, and the row is where they find that out.
 */
export type SupportMeetingStatus =
  | "Scheduled"
  | "Rescheduled"
  | "Completed"
  | "Cancelled";

/**
 * Whether the call has yet to happen — which is what decides everything the
 * screen does about it: whether there is a room to join, a booking to move, and
 * whether the form above the history will take another.
 *
 * Asked of the status rather than written out at each of those three, so that a
 * fourth word for a call that is still ahead is added in one place.
 */
export const isUpcomingStatus = (status: SupportMeetingStatus): boolean =>
  status === "Scheduled" || status === "Rescheduled";

export interface SupportMeeting {
  id: string;
  status: SupportMeetingStatus;
  /** When it starts, as a full ISO timestamp — a call is a time, not a day. */
  startsAt: string;
  /**
   * When it is over. What decides that a call still booked as `SCHEDULED` has
   * in fact been and gone — see `toSupportMeeting`, which is the only place
   * that matters.
   */
  endsAt?: string;
  /**
   * The video room, where there is one. Absent on a call that has been and
   * gone or been called off: the link outlives neither, and a joinable-looking
   * button that opens an empty room is the worse half of both.
   */
  joinUrl?: string;
  /**
   * Cal's own id for this booking, which is what its booker takes to open on
   * an existing call rather than an empty one — see `BookCallPage`, where it
   * becomes `rescheduleUid`.
   *
   * The id rather than the reschedule link Cal also hands out: Modify opens our
   * own booking screen now, and that screen builds the booker itself. Absent on
   * a call that has been or been called off, which leaves nothing to move.
   */
  rescheduleUid?: string;
  /**
   * Why a call was called off, in the words of whoever called it off. Only ever
   * set on a `Cancelled` row, and often not even then — cancelling asks for a
   * reason and does not insist on one.
   */
  cancellationReason?: string;
  /**
   * What the call is about, in the words of the support form's own list —
   * "Account setup", "Billing and payments". It is what titles the row.
   *
   * The first half of the booking's notes, which is where the form put it on
   * the way to Cal. Absent on a booking made anywhere else, since only that
   * form asks the question — see `splitNotes`.
   */
  reason?: string;
  /**
   * What the booker wrote under that reason: the paragraph the support form
   * collects, sent to Cal as the rest of the booking's notes. Absent where
   * they left it blank, and on most rescheduled rows — Cal does not carry the
   * note across when a call is moved.
   *
   * Kept on every row, whatever the status. On a call still ahead it is what
   * the call is about; on one that has been it is why it was asked for.
   */
  notes?: string;
}

/**
 * One booking as `GET /api/v1/support-history/` sends it: Cal's own record,
 * relayed by our backend, snake_case and all.
 *
 * Every field is optional to read even where the endpoint always sends it. This
 * is a third party's data passing through a relay, and a history that throws
 * because one row of six arrived without a `meeting_url` would take the whole
 * tab down over a link nobody was going to click.
 */
export interface SupportMeetingResponse {
  cal_uid: string;
  scheduled_start: string;
  scheduled_end?: string | null;
  /** Cal's own, upper-cased: `SCHEDULED`, `CANCELLED`, … */
  status?: string | null;
  meeting_url?: string | null;
  /** Cal's own page for moving it. Not linked to — see `toSupportMeeting`. */
  modify_url?: string | null;
  /** Empty string rather than null where nobody gave one. */
  cancellation_reason?: string | null;
  /**
   * The whole of what the support form sent to Cal: the reason, a blank line,
   * then the message. Empty string where the booking was made without either,
   * and empty on most moved bookings — Cal starts the new one clean. Taken
   * apart again by `splitNotes`.
   */
  notes?: string | null;
  /**
   * The `cal_uid` of the booking this one replaced, where it replaced one, and
   * `null` where it is an original. Its presence is the whole of what makes a
   * row read `Rescheduled` — the booking it points at is a superseded record
   * the history does not list, so nothing here follows it.
   */
  rescheduled_from?: string | null;
}

/** The envelope the list arrives in. Not a page — there is nothing to page. */
export interface SupportHistoryResponse {
  support_history?: SupportMeetingResponse[] | null;
}
